/**
 * Writes for pending Iris cameras: persisting, deleting and choosing the bridge
 * for the studio-only records described in `device/transitory-record.ts`.
 *
 * Each write is optimistic (this client's copy of the bridge state changes at
 * once) and followed by a snapshot save, so a reload or an app restart restores
 * the bridge from a snapshot that already reflects it.
 *
 * Per rule: wire-contract-changes-must-not-break-old-firmware (ai.knowledge/global)
 * — records are studio-only: never a control.device entry, model or URI.
 */
import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { broker } from '#~/agent/broker.js';
import type { Proxy } from '#~/agent/types.js';
import { depot } from '#~/depot/index.js';
import type { Device } from '#~/device/index.js';
import type { Discovery } from '#~/device/discovery/index.js';
import {
  getTransitoryDevice,
  setTransitoryDevice,
  unsetTransitoryDevice,
  useTransitoryStore,
  type TransitoryPlaceholder
} from '#~/device/transitory.js';
import { toPendingAuthRecord } from '#~/device/transitory-record.js';
import { StudioError } from '#~/error/index.js';
import { logger } from '#~/logger/index.js';

/** The parts of a bridge proxy a pending-camera write uses. */
export type PendingCameraHost = Pick<Proxy, 'id' | 'mutate' | 'save'>;

/** Resolves a bridge id to its proxy in this Studio. */
export type AcquirePendingCameraHost = (agentId: Agent['id']) => T_Maybe<PendingCameraHost>;

/** A bridge that could hold a pending camera. */
export interface PendingCameraHostCandidate {
  readonly id: Agent['id'];
  readonly embedded: boolean;
  readonly online: boolean;
}

/** Longest a deletion may hold up a caller that reloads the Studio next. */
export const PENDING_CAMERA_REMOVE_TIMEOUT_MS = 8_000;

const acquireFromBroker: AcquirePendingCameraHost = (agentId) => broker.acquire(agentId);

/** Write a pending camera's record to its bridge and save the snapshot. */
export const addPendingCamera = async (
  host: PendingCameraHost,
  placeholder: Pick<TransitoryPlaceholder, 'id' | 'name' | 'createdAt'>,
  token: string
): Promise<void> => {
  await host.mutate(
    { control: { studio: { device: { [placeholder.id]: toPendingAuthRecord(placeholder) } } } },
    {},
    true
  );
  await host.save(token);
};

/** Delete a pending camera's record from its bridge and save the snapshot. */
export const removePendingCamera = async (
  host: PendingCameraHost,
  id: Device['id'],
  token: string
): Promise<void> => {
  // Optimistic, so this client's state drops the record before the save reads it.
  await host.mutate({}, { control: { studio: { device: { [id]: {} } } } }, true);
  await host.save(token);
};

/**
 * The bridge to persist a pending camera on: an online bridge that is not a
 * camera's own agent, preferring one that discovered the camera. Sorted, so
 * every Studio client picks the same bridge for the same camera.
 */
export const selectPendingCameraHost = (
  discovery: Pick<Discovery, 'agentId'> & { readonly agentIds?: T_Maybe<readonly Agent['id'][]> },
  candidates: readonly PendingCameraHostCandidate[]
): T_Maybe<Agent['id']> => {
  const eligible = candidates
    .filter((candidate) => candidate.online && !candidate.embedded)
    .map((candidate) => candidate.id)
    .sort();
  const seenBy = new Set<Agent['id']>([
    ...(discovery.agentIds ?? []),
    ...(discovery.agentId ? [discovery.agentId] : [])
  ]);

  return eligible.find((id) => seenBy.has(id)) ?? eligible[0];
};

/** The bridges of a room, as host candidates. */
export const pendingCameraHostCandidates = (roomId: Room['id']): PendingCameraHostCandidate[] => {
  const { agents, rooms } = depot.getState();

  return (rooms[roomId]?.agentIds ?? []).map((id) => ({
    id,
    embedded: !!agents[id]?.embedded,
    online: agents[id]?.status === 'online'
  }));
};

/** Unbind a placeholder whose record never reached its bridge, keeping it in memory. */
const keepInMemory = (id: Device['id']): void => {
  const current = getTransitoryDevice(id);

  if (!current?.hostAgentId) {
    return;
  }

  const { hostAgentId: _unbound, ...inMemory } = current;
  setTransitoryDevice(inMemory);
};

/**
 * Persist a just-recorded pending camera on a bridge of its room. Returns the
 * bridge it is written to, or `undefined` when none is eligible (the camera
 * then stays in memory for this session). The write runs in the background so
 * bulk-add never blocks; if it fails, the placeholder falls back to memory.
 */
export const persistPendingCamera = (
  placeholder: TransitoryPlaceholder,
  options: {
    readonly candidates: readonly PendingCameraHostCandidate[];
    readonly token: string;
    readonly acquire?: AcquirePendingCameraHost;
  }
): T_Maybe<Agent['id']> => {
  // Records are rebuilt as Iris cameras (their key is the Iris link endpoint).
  if (placeholder.transitoryKind !== 'iris') {
    return undefined;
  }

  const hostId = selectPendingCameraHost(placeholder.originalDiscovery, options.candidates);
  const host = hostId ? (options.acquire ?? acquireFromBroker)(hostId) : undefined;

  if (!host) {
    logger.info(
      `No bridge can persist pending camera ${placeholder.name}; it is kept until the app restarts.`
    );
    return undefined;
  }

  addPendingCamera(host, placeholder, options.token).catch((cause: unknown) => {
    keepInMemory(placeholder.id);
    logger.warning(
      new StudioError(`Unable to persist pending camera ${placeholder.name}.`, { cause })
    );
  });

  return host.id;
};

/** Settle with `work`, or after `ms` (`work` keeps running, its failure still observed). */
const settleWithin = async (work: Promise<void>, ms: number): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<void>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error(`timed out after ${ms} ms`)), ms);
  });

  try {
    await Promise.race([work, deadline]);
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Remove a pending camera: its tile, and the record its bridge persists (if
 * any), waiting at most `timeoutMs`. The token is only requested when a record
 * must be deleted. Always settles; a failed deletion is logged and leaves the
 * tile, since the record (and so the camera) still exists.
 * Per rule: settle-every-terminal-branch (ai.knowledge/global).
 */
export const removeTransitoryCamera = async (
  id: Device['id'],
  getToken: () => Promise<string>,
  options: { readonly acquire?: AcquirePendingCameraHost; readonly timeoutMs?: number } = {}
): Promise<void> => {
  const placeholder = getTransitoryDevice(id);
  const host = placeholder?.hostAgentId
    ? (options.acquire ?? acquireFromBroker)(placeholder.hostAgentId)
    : undefined;

  if (!host) {
    if (placeholder?.hostAgentId) {
      logger.info(
        `Bridge of pending camera ${placeholder.name} is not in this Studio; tile removed.`
      );
    }
    unsetTransitoryDevice(id);
    return;
  }

  const work = (async () => {
    const token = await getToken();
    // No await between dropping the tile and the optimistic delete, so a bridge
    // tick cannot re-add the tile from the not-yet-deleted record.
    unsetTransitoryDevice(id);
    await removePendingCamera(host, id, token);
  })();
  // A deletion that outlives the deadline must not surface as an unhandled rejection.
  work.catch(() => undefined);

  try {
    await settleWithin(work, options.timeoutMs ?? PENDING_CAMERA_REMOVE_TIMEOUT_MS);
  } catch (cause) {
    logger.warning(
      new StudioError(`Unable to delete pending camera ${placeholder?.name ?? id}.`, { cause })
    );
  }
};

/** Remove every pending Iris camera at `address` (it was just linked through single add). */
export const removeIrisTransitoryCamerasAt = async (
  address: T_Maybe<string>,
  getToken: () => Promise<string>,
  options?: { readonly acquire?: AcquirePendingCameraHost; readonly timeoutMs?: number }
): Promise<void> => {
  if (!address) {
    return;
  }

  const ids = Object.values(useTransitoryStore.getState().placeholders)
    .filter(
      (placeholder) => placeholder.transitoryKind === 'iris' && placeholder.address === address
    )
    .map((placeholder) => placeholder.id);

  await Promise.all(ids.map((id) => removeTransitoryCamera(id, getToken, options)));
};
